import { APP_INITIALIZER, ApplicationConfig, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideOAuthClient } from 'angular-oauth2-oidc';

import { routes } from './app.routes';
import { OasisAuthService } from './core/auth/oasis-auth.service';
import { SessionIdleService } from './core/auth/session-idle.service';
import { OasisConfigService } from './core/cycle/oasis-config.service';
import { authInterceptor } from './core/auth/auth.interceptor';

export const appConfig: ApplicationConfig = {
	providers: [
		provideBrowserGlobalErrorListeners(),
		// Scroll the viewport to the top on every forward navigation (each section
		// starts at the top after Save & Continue / Back / guard redirects) instead
		// of keeping the previous scroll position from a long page.
		provideRouter(routes, withInMemoryScrolling({ scrollPositionRestoration: 'top' })),
		provideHttpClient(withInterceptors([authInterceptor])),
		provideOAuthClient(),
		{
			provide: APP_INITIALIZER,
			multi: true,
			deps: [OasisConfigService],
			// Cycle dates, the OMB notice and the J.D. cutoff, fetched from the public
			// config endpoint so they are not compiled into this bundle. `load()` never
			// rejects — an initializer that throws white-screens the app — and every
			// consumer has a documented fallback, so a failed fetch degrades to the
			// app's previous behaviour rather than breaking it.
			//
			// Deliberately not ordered against the auth initializer below: the config
			// route is unauthenticated, and `authInterceptor` skips a request when there
			// is no token rather than sending an empty bearer.
			useFactory: (config: OasisConfigService) => () => config.load(),
		},
		{
			provide: APP_INITIALIZER,
			multi: true,
			deps: [OasisAuthService],
			useFactory: (auth: OasisAuthService) => () => auth.init(),
		},
		{
			provide: APP_INITIALIZER,
			multi: true,
			deps: [SessionIdleService],
			// Instantiating the service IS the wiring — its constructor registers an
			// effect on the auth signal, which starts and stops the idle watch in step
			// with the auth state. Nothing needs to call it, so the initializer exists
			// only to construct it eagerly rather than on first injection.
			useFactory: (_sessionIdle: SessionIdleService) => () => undefined,
		},
	],
};
